/**
 * Enhance Scope v2 (B-531) slice 3 — supplement-justification serializer.
 *
 * Turns the set of APPLIED proposals into a plain-text memo an adjuster can
 * paste into a carrier supplement: per room, each addition/swap with its
 * quantity, plain-English reason, the trigger (a quoted narrative excerpt when
 * the trigger came from the notes), and the citation (or "No citation").
 *
 * Pure + deterministic so it can be unit-tested without the DOM. `choiceFor`
 * resolves which MODIFY candidate the user applied (defaults to default_index).
 */
import type { EnhanceProposal } from '../../../types';

const HOUSE_LABEL = 'House-wide / overall';

/** Resolve the chosen MODIFY candidate index for an applied proposal. */
export type ChoiceResolver = (_proposal: EnhanceProposal) => number;

/** The trigger line — a quoted excerpt for narrative triggers, else the raw
 *  detail. */
function triggerLine(p: EnhanceProposal): string {
  if (p.trigger.type === 'narrative') {
    return `Trigger: "${p.trigger.detail}"`;
  }
  return `Trigger: ${p.trigger.detail}`;
}

/** The citation line, or the explicit "No citation" fallback. */
function citationLine(p: EnhanceProposal): string {
  return `Citation: ${p.citation ? p.citation : 'No citation'}`;
}

/**
 * Serialize the applied proposals into a supplement-justification memo.
 *
 * Args:
 *   applied (EnhanceProposal[]): proposals to include (already filtered to the
 *     ones the user applied). Order is preserved within each room group.
 *   choiceFor (ChoiceResolver): resolves a MODIFY's chosen candidate index.
 *
 * Returns:
 *   string: the plain-text memo (empty string when `applied` is empty).
 */
export function buildSupplementMemo(
  applied: EnhanceProposal[],
  choiceFor: ChoiceResolver,
): string {
  if (applied.length === 0) return '';

  // Group by room, preserving first-seen order; roomless → House-wide.
  const order: string[] = [];
  const byRoom = new Map<string, EnhanceProposal[]>();
  for (const p of applied) {
    const room = p.room ?? p.modify?.room ?? HOUSE_LABEL;
    const arr = byRoom.get(room);
    if (arr) arr.push(p);
    else {
      byRoom.set(room, [p]);
      order.push(room);
    }
  }

  const out: string[] = ['Supplement justification', ''];
  for (const room of order) {
    out.push(`=== ${room} ===`);
    for (const p of byRoom.get(room)!) {
      if (p.kind === 'MODIFY' && p.modify) {
        const idx = choiceFor(p);
        const chosen =
          p.modify.candidates[idx] ?? p.modify.candidates[p.modify.default_index];
        const to = chosen ? `${chosen.cat} ${chosen.sel}`.trim() : `${p.cat} ${p.sel}`.trim();
        out.push(
          `Changed ${p.modify.current_cat} ${p.modify.current_sel} → ${to}`,
        );
        out.push(`Reason: ${p.reason}`);
      } else {
        out.push(`${p.cat} ${p.sel} — ${p.description}`.trim());
        const qty = p.quantity != null ? `${p.quantity} ${p.unit}`.trim() : p.unit;
        if (qty) out.push(qty);
        out.push(`Reason: ${p.reason}`);
      }
      out.push(triggerLine(p));
      out.push(citationLine(p));
      out.push('');
    }
  }
  // Trim the trailing blank line.
  while (out.length > 0 && out[out.length - 1] === '') out.pop();
  return out.join('\n');
}

export default buildSupplementMemo;
